import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ResetPasswordFormSchema, resetPasswordSchema } from './formSchema';
import { AuthenticationBaseLayout } from '../../components/ui/authentication/authenticationBaseLayout';
import { useToast } from '@/hooks/useToast';
import { useResetPassword } from '@/services/api/users/mutations';
import { AxiosError } from 'axios';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { InfoIcon } from 'lucide-react';

export const ResetPassword = () => {
  const form = useForm<ResetPasswordFormSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid');
  const token = searchParams.get('token');
  const newAccount = searchParams.get('new_account');
  const isNewAccount = newAccount?.toLowerCase() === 'true';
  const title = isNewAccount ? 'Create Your Password' : 'Reset Your Password';
  const description = isNewAccount
    ? 'Create a new password for your account below.'
    : 'Enter your new password below.';
  const successTitle = isNewAccount
    ? 'Password created.'
    : 'Password reset successfully.';

  const successDescription = isNewAccount
    ? 'Your password has been created. You can now sign in using your new password.'
    : 'Your password has been updated. You can now sign in using your new password.';

  const { mutate: resetPassword, isPending } = useResetPassword(
    () => {
      toast({
        title: successTitle,
        description: successDescription,
      });
      navigate('/login');
    },
    (error) => {
      toast({
        title: 'Error',
        // Show the API's message as written. Title-casing it turned
        // "Invalid token or user ID." into "Invalid Token Or User Id."
        description:
          (error as AxiosError<{ error: string }>)?.response?.data?.error ??
          'Something went wrong',
      });
      console.error(error);
    }
  );

  const onSubmit = (data: { password: string; confirmPassword: string }) => {
    resetPassword({
      uid: uid as string,
      token: token as string,
      password: data.password,
      confirmPassword: data.confirmPassword,
    });
    form.reset();
  };

  return (
    <AuthenticationBaseLayout>
      <div className="flex flex-col items-start w-full max-w-sm p-6 lg:w-auto lg:max-w-none">
        <div className="flex flex-col justify-start items-start mb-4 text-left">
          <h1 className="text-base font-bold text-card-foreground">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>

        <Form {...form}>
          <form
            className="mt-2 space-y-4 w-full lg:w-auto"
            onSubmit={form.handleSubmit(onSubmit as SubmitHandler<FieldValues>)}
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="text-left">
                  <FormLabel className="text-card-foreground text-sm flex items-center gap-2">
                    New Password *
                    <PasswordRequirementTooltip />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="w-full lg:w-[352px]"
                      placeholder="*********"
                      type="password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="text-left">
                  <FormLabel className="text-card-foreground text-sm flex items-center gap-2">
                    Confirm Password *
                    <PasswordRequirementTooltip />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="w-full lg:w-[352px]"
                      placeholder="*********"
                      type="password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={
                  isPending ||
                  !form.formState.isValid ||
                  form.formState.isSubmitting
                }
              >
                {isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </AuthenticationBaseLayout>
  );
};

export const PasswordRequirementTooltip = () => {
  return (
    <Tooltip>
      <TooltipTrigger>
        <InfoIcon className="h-4 w-4 text-muted-foreground" />
      </TooltipTrigger>
      <TooltipContent
        side="right"
        className="bg-background border border-border"
      >
        <div className="flex flex-col gap-2 p-2 text-foreground">
          <p className="font-bold">Password must contain at least:</p>
          <ul className="list-disc list-inside">
            <li>8 characters</li>
            <li>1 uppercase letter</li>
            <li>1 lowercase letter</li>
            <li>1 number</li>
            <li>1 special character</li>
          </ul>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
